import { activityConfig } from '@ark/predict-shared';
import type {
  QuoteSnapshotResponse,
  MarketDetailResponse,
  MarketStatsResponse,
  SubmitGuessResponse,
  LeaderboardResponse,
  SetStockResponse,
} from '@ark/predict-shared';
import { BaseApiClient } from '@ark/web-api/frontend';

export type {
  MarketDetailResponse,
  MarketStatsResponse,
  SubmitGuessResponse,
  LeaderboardResponse,
  SetStockResponse,
  QuoteSnapshotResponse,
};

export class PredictApiClient extends BaseApiClient {
  constructor() {
    super({
      apiPrefix: activityConfig.apiPrefix,
    });
  }

  async getMarketDetail(marketId: string): Promise<MarketDetailResponse> {
    return this.get<MarketDetailResponse>('market', { marketId });
  }

  async getMarketStats(marketId: string): Promise<MarketStatsResponse> {
    return this.get<MarketStatsResponse>('market/stats', { marketId });
  }

  async submitGuess(
    marketId: string,
    answerValue: string,
    reason?: {
      presetCode?: string;
      customText?: string;
      label?: Record<string, string>;
    },
  ): Promise<SubmitGuessResponse> {
    return this.post<SubmitGuessResponse>('guess/submit', {
      marketId,
      answerValue,
      reason,
    });
  }

  async getLeaderboard(
    actPlaybookId: string,
    includeSelf = true,
  ): Promise<LeaderboardResponse> {
    return this.get<LeaderboardResponse>('leaderboard', {
      actPlaybookId,
      includeSelf: includeSelf ? '1' : '0',
    });
  }

  async getWatchStatus(
    stockIds: string[],
  ): Promise<{ watchMap: Record<string, boolean> }> {
    if (!stockIds || stockIds.length === 0) {
      return { watchMap: {} };
    }
    return this.get<{ watchMap: Record<string, boolean> }>('watch-status', {
      stockIds: stockIds.join(','),
    });
  }

  async addToWatchList(stockIds: string[]): Promise<SetStockResponse> {
    return this.post<SetStockResponse>('watch-list/add', { stockIds });
  }

  async removeFromWatchList(stockIds: string[]): Promise<SetStockResponse> {
    return this.post<SetStockResponse>('watch-list/remove', { stockIds });
  }

  async getQuoteSnapshot(
    securityId: number,
    lang: number,
  ): Promise<QuoteSnapshotResponse> {
    return this.get<QuoteSnapshotResponse>('quote-snapshot', {
      securityId: String(securityId),
      lang: String(lang),
    });
  }
}

export const apiClient = new PredictApiClient();
